import { useCustom } from "@refinedev/core";

// Modulebeheer (Epic 20) Jmix-exit: the SPA reads organisation-wide module availability from
// GET /api/modules (any authenticated user) to gate role-gated UI on "module enabled", mirroring the
// Jmix RelatieEntryView (a tab shows iff the relation has the role AND the module is on). The shared
// MSSQL `module_setting` row is the source of truth; this endpoint resolves it (incl. the parent
// cascade, e.g. pleeggezin/donateur off when asiel is off) and the registry/config defaults.

export interface ModuleInfo {
  key: string;
  /** Effective (cascade-resolved) availability — what consumers gate on. */
  enabled: boolean;
  /** Own toggle state (override row → registry default) — the admin-screen checkbox value. */
  ownEnabled: boolean;
  defaultEnabled: boolean;
  parentKey: string | null;
  userManageable: boolean;
  labelKey: string;
  descriptionKey: string;
  groupKey: string;
}

export interface ModuleAvailability {
  /**
   * Whether a module is enabled. PERMISSIVE by design: returns true while the feed is still loading,
   * unavailable, or doesn't know the key — so a transient fetch failure never hides UI. Only a module
   * the feed explicitly reports as disabled returns false (the actual gate).
   */
  isModuleEnabled: (key: string) => boolean;
  modules: ModuleInfo[] | null;
  isLoading: boolean;
}

export function useModuleAvailability(): ModuleAvailability {
  const { data, isLoading } = useCustom<ModuleInfo[]>({ url: "modules", method: "get" });
  const raw = data?.data;
  const modules = Array.isArray(raw) ? raw : null;
  const known = modules ? new Set(modules.map((m) => m.key)) : null;
  const enabledKeys = modules ? new Set(modules.filter((m) => m.enabled).map((m) => m.key)) : null;

  const isModuleEnabled = (key: string): boolean => {
    if (!known || !known.has(key)) return true;
    return enabledKeys!.has(key);
  };

  return { isModuleEnabled, modules, isLoading };
}
